import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Sunrise, Sunset, Clock } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import { pickLang, RTL_LANGS } from '@/utils/localizedStrings';
import { tooltipText } from '@/utils/tooltipStrings';

const AstronomyPanel = ({ sunPosition, timezone, surfaceColors, textColors }) => {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);

  if (!sunPosition) {
    return (
      <div
        className="p-6 rounded-2xl backdrop-blur-2xl border"
        style={{
          background: surfaceColors.background,
          borderColor: surfaceColors.border
        }}
        data-testid="astronomy-panel"
      >
        <div style={{ color: textColors.secondary }}>{t('loading')}</div>
      </div>
    );
  }

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const opts = { hour: '2-digit', minute: '2-digit' };
    if (timezone) opts.timeZone = timezone;
    return date.toLocaleTimeString(
      i18n.language === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-US',
      opts
    );
  };

  // Tooltip explanations — translated per current language
  const TIPS = {
    sunrise:   tooltipText('sunrise',     lang),
    solarNoon: tooltipText('solarNoon',   lang),
    sunset:    tooltipText('sunset',      lang),
    dayLength: tooltipText('dayLength',   lang),
    altitude:  tooltipText('altitudeSun', lang),
    azimuth:   tooltipText('azimuth',     lang),
  };

  // Reusable row: icon + label + (ⓘ) + value
  const InfoRow = ({ icon: Icon, label, value, testId, tooltipKey, tooltipTestId, isLast }) => (
    <div
      className={`flex items-center justify-between ${isLast ? '' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={22} style={{ color: textColors.accent, flexShrink: 0 }} />
        <span style={{ color: textColors.secondary }}>{label}</span>
        <InfoTooltip testId={tooltipTestId} text={TIPS[tooltipKey]} />
      </div>
      <span
        className="font-medium"
        style={{ color: textColors.primary, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div
      className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border"
      style={{
        background: surfaceColors.background,
        borderColor: surfaceColors.border
      }}
      data-testid="astronomy-panel"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <h3
        className="text-heading text-lg md:text-xl mb-6"
        style={{ color: textColors.primary }}
      >
        {t('astronomicalData')}
      </h3>

      <div className="space-y-4">
        <InfoRow
          icon={Sunrise}
          label={t('sunrise')}
          value={formatTime(sunPosition.sunrise)}
          testId="sunrise-info"
          tooltipKey="sunrise"
          tooltipTestId="tooltip-sunrise"
        />
        <InfoRow
          icon={Sun}
          label={t('solarNoon')}
          value={formatTime(sunPosition.solar_noon)}
          testId="solar-noon-info"
          tooltipKey="solarNoon"
          tooltipTestId="tooltip-solar-noon"
        />
        <InfoRow
          icon={Sunset}
          label={t('sunset')}
          value={formatTime(sunPosition.sunset)}
          testId="sunset-info"
          tooltipKey="sunset"
          tooltipTestId="tooltip-sunset"
        />

        {/* Day Length */}
        <div
          className="flex items-center justify-between pt-4 border-t"
          style={{ borderColor: surfaceColors.border }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Clock size={22} style={{ color: textColors.accent, flexShrink: 0 }} />
            <span style={{ color: textColors.secondary }}>{t('dayLength')}</span>
            <InfoTooltip testId="tooltip-day-length" text={TIPS.dayLength} />
          </div>
          <span
            className="font-medium"
            style={{ color: textColors.primary, fontVariantNumeric: 'tabular-nums' }}
            data-testid="day-length"
          >
            {sunPosition.day_length_hours.toFixed(1)} {t('hours')}
          </span>
        </div>

        {/* Sun Position Details */}
        <div
          className="mt-6 pt-6 border-t space-y-2"
          style={{ borderColor: surfaceColors.border }}
        >
          <div
            className="text-xs text-caption mb-3"
            style={{ color: textColors.accent }}
          >
            {t('sunPosition')}
          </div>

          <div className="flex justify-between text-sm text-body items-center">
            <div className="flex items-center gap-2">
              <span style={{ color: textColors.secondary }}>{t('altitude')}</span>
              <InfoTooltip testId="tooltip-altitude-sun" text={TIPS.altitude} />
            </div>
            <span
              className="tabular-nums mono-text"
              style={{ color: textColors.primary }}
              data-testid="sun-altitude"
            >
              {sunPosition.altitude.toFixed(2)}°
            </span>
          </div>

          <div className="flex justify-between text-sm text-body items-center">
            <div className="flex items-center gap-2">
              <span style={{ color: textColors.secondary }}>{t('azimuth')}</span>
              <InfoTooltip testId="tooltip-azimuth" text={TIPS.azimuth} />
            </div>
            <span
              className="tabular-nums mono-text"
              style={{ color: textColors.primary }}
              data-testid="sun-azimuth"
            >
              {sunPosition.azimuth.toFixed(2)}°
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AstronomyPanel;
